"use client";

import { useMemo } from "react";

const GREEN = "#A8FBBE";

const RED = "#F74B48";

function GradientTextParser({ text }: { text: string }) {
  return useMemo(() => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, lineIndex) => {
      const parts = line.split(/(`[^`]*`)|(\*[^*]*\*)/g).filter(Boolean);
      return (
        <span key={lineIndex}>
          {lineIndex > 0 && <br />}
          {parts.map((part, index) => {
            if (part.startsWith("`") && part.endsWith("`")) {
              const inner = part.slice(1, -1);
              const isItalic = inner.startsWith("*") && inner.endsWith("*");
              const content = isItalic ? inner.slice(1, -1) : inner;
              return (
                <span
                  key={index}
                  className={`bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat${isItalic ? " italic" : ""}`}
                >
                  {content}
                </span>
              );
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return <em key={index}>{part.slice(1, -1)}</em>;
            }
            return part;
          })}
        </span>
      );
    });
  }, [text]);
}

type Row = { label: string; value: string; _key?: string };

type Column = {
  title?: string;
  rows?: Row[];
};

export interface StatsComparisonProps {
  tag?: string;
  heading?: string;
  subHeading?: string;
  leftColumn?: Column;
  rightColumn?: Column;
  footer?: string;
  paddingTop?: string;
  paddingBottom?: string;
}

export default function StatsComparison({
  tag,
  heading,
  subHeading,
  leftColumn,
  rightColumn,
  footer,
  paddingTop = "none",
  paddingBottom = "none",
}: StatsComparisonProps) {
  const ptClass = `section-pt-${paddingTop ?? "none"}`;
  const pbClass = `section-pb-${paddingBottom ?? "none"}`;
  return (
    <section className={`container mx-auto px-4 xl:px-[54px] ${ptClass} ${pbClass}`}>
      <div className="flex flex-col items-center gap-6 text-center">
        {tag && (
          <span className="font-mono text-[9px] font-semibold tracking-widest uppercase leading-none whitespace-nowrap p-2 rounded-sm bg-grey-900 text-grey-400">
            {tag}
          </span>
        )}
        {heading && (
          <h2
            className="text-white font-normal m-0 leading-[130%] text-[36px] max-w-3xl"
            style={{ fontFamily: "var(--font-roobert)" }}
          >
            <GradientTextParser text={heading} />
          </h2>
        )}
        {subHeading && (
          <p className="text-base leading-6 tracking-wide m-0 max-w-xl text-grey-550">
            {subHeading}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 mt-12 max-w-5xl mx-auto w-full">
        {leftColumn && (
          <div className="flex flex-col gap-0">
            {leftColumn.title && (
              <p className="font-semibold text-[18px] m-0 mb-5 uppercase" style={{ color: GREEN, fontFamily: "var(--font-roobert)" }}>
                {leftColumn.title}
              </p>
            )}
            {(leftColumn.rows ?? []).map((row, i) => (
              <div key={row._key ?? i} className="flex items-center justify-between py-4" style={{ borderTop: "1px solid #2C2C2C" }}>
                <span className="text-[15px]" style={{ color: "#9E9E9E" }}>{row.label}</span>
                <span className="text-[22px] font-bold tabular-nums" style={{ color: GREEN, fontFamily: "var(--font-roobert)" }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        )}
        {rightColumn && (
          <div className="flex flex-col gap-0">
            {rightColumn.title && (
              <p className="font-semibold text-[18px] m-0 mb-5 uppercase" style={{ color: RED, fontFamily: "var(--font-roobert)" }}>
                {rightColumn.title}
              </p>
            )}
            {(rightColumn.rows ?? []).map((row, i) => (
              <div key={row._key ?? i} className="flex items-center justify-between py-4" style={{ borderTop: "1px solid #2C2C2C" }}>
                <span className="text-[15px]" style={{ color: "#9E9E9E" }}>{row.label}</span>
                <span className="text-[22px] font-bold tabular-nums" style={{ color: RED, fontFamily: "var(--font-roobert)" }}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {footer && (
        <p className="text-[12px] m-0 mt-6 text-center" style={{ color: "#6E6E6E" }}>
          {footer}
        </p>
      )}
    </section>
  );
}
